import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { domainKeys } from '@/shared/lib/query/queryKeys';
import type { OwnerEditHook } from '@/shared/types/editHooks';

import type { DomainItem, DomainStakeholder } from '@/entities/domain';
import type { TeamOwner, UserOwner } from '@/entities/owner';
import { buildNewOwners, buildOwnerOperations, patchOwners } from '@/entities/owner';
import { useTeamOwners, useUserOwners } from '@/entities/owner/model';

type UseDomainOwnerEditProps = {
  domainId: string;
  domainFqn: string;
};

export const useDomainOwnerEdit = ({ domainId, domainFqn }: UseDomainOwnerEditProps): OwnerEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const domainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
  const currentOwners = useMemo(() => domainData?.owners || [], [domainData?.owners]);

  const { data: teamOwners = [], isLoading: isLoadingTeams } = useTeamOwners();
  const { data: userOwners = [], isLoading: isLoadingUsers } = useUserOwners();

  const updateOwnersMutation = useMutation({
    mutationFn: async ({
      selectedTeamId,
      selectedUserIds,
      teams,
      users,
      currentOwners,
    }: {
      selectedTeamId: string | null;
      selectedUserIds: string[];
      teams: TeamOwner[];
      users: UserOwner[];
      currentOwners: DomainStakeholder[];
    }) => {
      const newOwners = buildNewOwners({
        selectedTeamId,
        selectedUserIds,
        teams,
        users,
      });
      const operations = buildOwnerOperations({
        newOwners,
        currentOwners,
      });
      await patchOwners({
        resourceId: domainId,
        apiPath: '/domains',
        operations,
      });
    },
    onMutate: async ({ selectedTeamId, selectedUserIds, teams, users }) => {
      await queryClient.cancelQueries({ queryKey: domainKeys.detail(domainFqn) });
      const previousDomain = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));

      if (previousDomain) {
        const newOwners: DomainStakeholder[] = [];

        if (selectedTeamId) {
          const team = teams.find((t) => t.id === selectedTeamId);
          if (team) {
            newOwners.push({
              id: team.id,
              type: 'team',
              name: team.name,
              fullyQualifiedName: team.fullyQualifiedName,
              displayName: team.displayName,
              deleted: false,
              href: '',
            });
          }
        }

        selectedUserIds.forEach((userId) => {
          const user = users.find((u) => u.id === userId);
          if (user) {
            newOwners.push({
              id: user.id,
              type: 'user',
              name: user.name,
              fullyQualifiedName: user.fullyQualifiedName,
              displayName: user.displayName,
              deleted: false,
              href: '',
            });
          }
        });

        queryClient.setQueryData<DomainItem>(domainKeys.detail(domainFqn), {
          ...previousDomain,
          owners: newOwners,
        });
      }

      return { previousDomain };
    },
    onError: (error, _variables, context) => {
      if (context?.previousDomain) {
        queryClient.setQueryData(domainKeys.detail(domainFqn), context.previousDomain);
      }
      notification.error({
        message: NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(domainFqn) });
      notification.success({
        message: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 2,
      });
    },
  });

  const currentTeamId = useMemo(
    () => currentOwners.find((owner) => owner.type === 'team')?.id || null,
    [currentOwners]
  );

  const currentUserIds = useMemo(
    () => currentOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id),
    [currentOwners]
  );

  const currentUserIdsString = currentUserIds.join(',');

  useEffect(() => {
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeamId, currentUserIdsString]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestDomainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
    const latestOwners = latestDomainData?.owners || [];

    if (latestOwners.length > 0) {
      const teamOwner = latestOwners.find((owner) => owner.type === 'team');
      const userOwnerIds = latestOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id);

      setSelectedTeamId(teamOwner?.id || null);
      setSelectedUserIds(userOwnerIds);
    }
  }, [queryClient, domainFqn]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleTeamSelect = useCallback((teamId: string) => {
    setSelectedTeamId((prev) => (prev === teamId ? null : teamId));
    setSelectedUserIds([]);
  }, []);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
    setSelectedTeamId(null);
  }, []);

  const handleUpdate = useCallback(() => {
    const latestDomainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
    const latestOwners = latestDomainData?.owners || [];

    updateOwnersMutation.mutate(
      {
        selectedTeamId,
        selectedUserIds,
        teams: teamOwners,
        users: userOwners,
        currentOwners: latestOwners,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      }
    );
  }, [selectedTeamId, selectedUserIds, teamOwners, userOwners, queryClient, domainFqn, updateOwnersMutation]);

  const hasChanges = useMemo(() => {
    const teamChanged = currentTeamId !== selectedTeamId;
    const usersChanged =
      currentUserIds.length !== selectedUserIds.length || !currentUserIds.every((id) => selectedUserIds.includes(id));

    return teamChanged || usersChanged;
  }, [currentTeamId, currentUserIds, selectedTeamId, selectedUserIds]);

  return {
    isOpen,
    activeTab,
    selectedTeamId,
    selectedUserIds,
    teamOwners,
    userOwners,
    currentOwners,
    isLoadingTeams,
    isLoadingUsers,
    isUpdating: updateOwnersMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    setActiveTab,
    handleTeamSelect,
    handleUserSelect,
    handleUpdate,
  };
};
